import { useTranslation } from 'react-i18next';
import { Controller, Path, useFormContext } from 'react-hook-form';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
} from '@payler/ui-components';
import { Box, HStack, Icon, Text, Input, FormControl, FormLabel } from '@chakra-ui/react';
import { FC } from 'react';
import { TextStyles } from '@payler/ui-theme';
import { CloseCircle14Icon } from '@payler/ui-icons';
import { FieldValues } from 'react-hook-form/dist/types';

type Props<F> = {
  /**
   * ключ локализации названия поля
   */
  fieldNameKey: string;
  /**
   * Поле формы
   */
  formFieldName: Path<F>;
  /**
   * Плейсхолдер для поля ввода
   */
  placeholder?: string;
  /**
   * Тип поля ввода
   */
  inputType?: 'text' | 'tel' | 'number';
  /**
   * Максимальная длина ввода
   */
  maxLength?: number;
  /**
   * Скрыть иконку очистки
   */
  hideClearIcon?: boolean;
  /**
   * Заблокировать фильтр (отображается серым)
   */
  isDisabled?: boolean;
};

/**
 * Аккордеон с полем ввода для отображения в фильтрах
 */
export const FiltersInputAccordion = <F extends FieldValues>({
  fieldNameKey,
  formFieldName,
  placeholder,
  inputType = 'text',
  maxLength,
  hideClearIcon,
  isDisabled,
}: Props<F>) => {
  return (
    <Accordion
      alignSelf="stretch"
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      <Header<F>
        fieldNameKey={fieldNameKey}
        formFieldName={formFieldName}
        hideClearIcon={hideClearIcon}
      />
      <AccordionBody p={2}>
        <Controller
          name={formFieldName}
          control={useFormContext<F>().control}
          render={({ field }) => {
            const { t } = useTranslation(['fields']);
            return (
              <FormControl isDisabled={isDisabled}>
                <Input
                  {...field}
                  placeholder={placeholder}
                  type={inputType}
                  maxLength={maxLength}
                  isDisabled={isDisabled}
                  onInput={inputType === 'number' && maxLength ? (e) => {
                    const target = e.target as HTMLInputElement;
                    const numericValue = target.value.replace(/[^0-9]/g, '').slice(0, maxLength);
                    target.value = numericValue;
                    field.onChange(numericValue);
                  } : undefined}
                />
              </FormControl>
            );
          }}
        />
      </AccordionBody>
    </Accordion>
  );
};

//region header
type HeaderProps<F> = Omit<Props<F>, 'placeholder'>;

const Header = <F extends FieldValues>({
  fieldNameKey,
  formFieldName,
  hideClearIcon,
}: HeaderProps<F>) => {
  const { t } = useTranslation(['fields']);
  const { control, setValue, watch } = useFormContext<F>();
  const value = watch(formFieldName);

  const handleClear = () => {
    setValue(formFieldName, '' as any);
  };

  const hasValue = value && value.toString().trim() !== '';

  return (
    <Controller
      control={control}
      name={formFieldName}
      render={() => (
        <AccordionHeader>
          <HStack justifyContent="space-between" w="full">
            <Text textStyle={TextStyles.Subtitle14Regular} color="primary.500">
              {t(`fields:${fieldNameKey}` as any)}
            </Text>
            <HStack spacing={2}>
              {hasValue && !hideClearIcon && (
                <Icon
                  as={CloseCircle14Icon}
                  w="14px"
                  h="14px"
                  color="primary.400"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
              )}
            </HStack>
          </HStack>
        </AccordionHeader>
      )}
    />
  );
};
//endregion
